<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const route = useRoute();
const store = useStore();
const iframeRef = ref(null);

const dashboardApp = computed(() => {
  const appId = route.params.id;
  return store.getters['dashboardApps/getRecords'].find(
    app => app.id === Number(appId)
  );
});

const isTypebot = computed(() => {
  return dashboardApp.value?.content?.some(
    item => item.model_app === 'typebot'
  );
});

const appUrl = computed(() => {
  if (!dashboardApp.value?.content?.[0]?.url) return '';
  return dashboardApp.value.content[0].url;
});

// Sem app (id inexistente, apagado ou falha na busca) a tela ficava em
// "carregando" para sempre. Só mostra carregando enquanto a busca roda.
const hasFetched = ref(false);
const isFetching = computed(
  () => store.getters['dashboardApps/getUIFlags']?.isFetching
);
const isLoading = computed(
  () =>
    isFetching.value ||
    (!hasFetched.value && !store.getters['dashboardApps/getRecords'].length)
);

// O e-mail do agente só vai para a origem do app configurado, nunca '*'.
const appOrigin = computed(() => {
  try {
    return new URL(appUrl.value).origin;
  } catch {
    return null;
  }
});

const fillTypebotEmail = () => {
  if (!isTypebot.value || !iframeRef.value) return;

  setTimeout(() => {
    try {
      const iframe = iframeRef.value;
      const email = store.getters['auth/currentUser']?.email || '';

      // Tenta acessar o documento do iframe (pode falhar devido a restrições de segurança)
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        const emailInput = doc.querySelector('input[name="email"]');
        if (emailInput) {
          emailInput.value = email;
          emailInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } catch (error) {
        // Alternativa usando postMessage
        if (!appOrigin.value) return;
        iframe.contentWindow.postMessage(
          {
            action: 'fill_email',
            email: email,
          },
          appOrigin.value
        );
      }
    } catch (e) {
      console.error('Erro ao preencher email no Typebot:', e);
    }
  }, 1500);
};

onMounted(async () => {
  if (!store.getters['dashboardApps/getRecords'].length) {
    await store.dispatch('dashboardApps/get');
  }
  hasFetched.value = true;
});

const onIframeLoad = () => {
  if (isTypebot.value) {
    fillTypebotEmail();
  }
};
</script>

<template>
  <div class="h-full w-full flex-1 overflow-hidden">
    <iframe
      v-if="appUrl"
      :src="appUrl"
      class="h-full w-full border-0"
      :title="dashboardApp?.title"
      ref="iframeRef"
      @load="onIframeLoad"
    />
    <div
      v-else-if="isLoading"
      class="h-full w-full flex items-center justify-center text-n-slate-10"
    >
      {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LOADING') }}
    </div>
    <div
      v-else
      data-testid="dashboard-app-not-found"
      class="h-full w-full flex items-center justify-center text-n-slate-11"
    >
      {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.NOT_FOUND') }}
    </div>
  </div>
</template>
